import classNames from "classnames";
import { getGridParamsFromLocation } from "components";
import { ICurrency } from "components/product-collection/product-collection-views/product-collection-grid-view/grid-renderers/pinned-row-renderers";
import { IGridData } from "helpers/collection";
import { IDoor, getDimensionFilters } from "helpers/door-collection";
import { getSkuOrId } from "helpers/product-collection";
import get from "lodash/get";
import * as React from "react";
import { IDoorTotals } from "reducers/collection";
import { CartValue } from "./cart-value";
import { DATATEST } from "constants/integration";
import { formatMessage } from "lib/globalize-util";

type RightSideProps = {
    location: any;
    recalculateAllocationBagTotal: boolean;
    doorTotals: IDoorTotals;
    doorTotalsFiltered: IDoorTotals;
    doors: IDoor[];
    isServerRollup: boolean;
    tileViewData: IGridData;
    tileGridData: IGridData;
    currency?: ICurrency;
};
export const RightSide = ({
    location,
    recalculateAllocationBagTotal,
    doorTotals,
    doors,
    isServerRollup,
    tileViewData,
    doorTotalsFiltered,
    tileGridData,
    currency,
}: RightSideProps) => {
    const { gridParams } = location.query;
    const areDimensionsBeingFiltered = React.useMemo(() => {
        const gridParams = getGridParamsFromLocation(location);
        const dimensionFilters = getDimensionFilters(gridParams);
        return (
            (!!dimensionFilters[0] && dimensionFilters[0].some((d) => !!d)) ||
            (!!dimensionFilters[1] && dimensionFilters[1].some((d) => !!d))
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gridParams]); // We should also show filtered totals row when Door/Deliveries are being filtered

    const totals = React.useMemo(() => {
        // Only show doors totals when doors fields are part of schema
        const canShowTotals = doors.length > 0;
        return {
            quantity: get(doorTotals, ["_total", "units"]),
            retail: get(doorTotals, ["_total", "retail"]),
            wholesale: get(doorTotals, ["_total", "wholesale"]),
            canShowTotals,
        };
    }, [doorTotals, doors]);

    const totalsFiltered = React.useMemo(() => {
        const canShowTotals = doors.length > 0;
        return {
            quantity: get(doorTotalsFiltered, ["_total", "units"]),
            retail: get(doorTotalsFiltered, ["_total", "retail"]),
            wholesale: get(doorTotalsFiltered, ["_total", "wholesale"]),
            canShowTotals,
        };
    }, [doorTotalsFiltered, doors]);

    const { data } = tileViewData;
    const uniqueItemCountFiltered = React.useMemo(() => {
        if (isServerRollup) {
            return 0;
        }

        const uniqueProductSet = data.reduce((productIdSet, item) => {
            const key = getSkuOrId(item);
            productIdSet.add(key);
            return productIdSet;
        }, new Set());
        return uniqueProductSet.size;
    }, [data, isServerRollup]);

    const uniqueItemCount = React.useMemo(() => {
        if (isServerRollup) {
            return 0;
        }
        const uniqueProductSet = tileGridData.data.reduce((productIdSet, item) => {
            const key = getSkuOrId(item);
            productIdSet.add(key);
            return productIdSet;
        }, new Set());
        return uniqueProductSet.size;
    }, [tileGridData, isServerRollup]);

    const percentageValue = ((totals.retail - totals.wholesale) / totals.retail) * 100;

    const percentageValueFiltered = ((totalsFiltered.retail - totalsFiltered.wholesale) / totalsFiltered.retail) * 100;

    const totalItemsCount = isServerRollup ? (doorTotals?._total?.totalRows ?? 0) : tileGridData.data.size;
    const totalProducts = isServerRollup ? (doorTotals?._total?.totalProducts ?? 0) : uniqueItemCount;

    const totalItemsCountFiltered = isServerRollup
        ? (doorTotalsFiltered?._total?.totalRows ?? 0)
        : tileViewData.data.size;
    const totalProductsFiltered = isServerRollup
        ? (doorTotalsFiltered?._total?.totalProducts ?? 0)
        : uniqueItemCountFiltered;

    const filteredRow = totalItemsCountFiltered !== totalItemsCount || areDimensionsBeingFiltered;
    return (
        <div
            className={classNames({
                righSideColumn: true,
                "righSideColumn--loading": recalculateAllocationBagTotal,
            })}
        >
            <div className={classNames("rightSide", filteredRow ? "gridContainerFiltered" : "gridContainer")}>
                {filteredRow && (
                    <>
                        <div className="item">
                            <span className="headerTitle">{formatMessage("FILTERED")}</span>{" "}
                        </div>
                        <div className="item custom">
                            <CartValue
                                dataTest={DATATEST.ASSORTMENTS.FOOTER.TOTAL_PRODUCTS}
                                label={formatMessage("Products")}
                                value={totalProductsFiltered}
                            />
                        </div>
                        <div className="item custom">
                            <CartValue
                                dataTest={DATATEST.ASSORTMENTS.FOOTER.TOTAL_ROWS}
                                label={formatMessage("Rows")}
                                value={totalItemsCountFiltered}
                            />{" "}
                        </div>
                        <div className="item custom">
                            {totalsFiltered.canShowTotals && (
                                <CartValue
                                    dataTest={DATATEST.ASSORTMENTS.FOOTER.TOTAL_UNITS}
                                    label={formatMessage("Units")}
                                    value={totalsFiltered.quantity}
                                />
                            )}
                        </div>
                        <div className="item custom">
                            {totalsFiltered.canShowTotals && (
                                <CartValue
                                    dataTest={DATATEST.ASSORTMENTS.FOOTER.TOTAL_COST}
                                    label={formatMessage("Cost")}
                                    value={totalsFiltered.wholesale}
                                    isCurrency={true}
                                    currency={currency}
                                />
                            )}
                        </div>
                        <div className="item custom">
                            {totalsFiltered.canShowTotals && (
                                <CartValue
                                    dataTest={DATATEST.ASSORTMENTS.FOOTER.TOTAL_RETAIL}
                                    label={formatMessage("Retail")}
                                    value={totalsFiltered.retail}
                                    isCurrency={true}
                                    currency={currency}
                                />
                            )}
                        </div>
                        <div className="item custom">
                            {totalsFiltered.canShowTotals && (
                                <CartValue
                                    dataTest={DATATEST.ASSORTMENTS.FOOTER.TOTAL_MARKUP}
                                    label={formatMessage("Markup")}
                                    value={totalsFiltered.retail > 0 ? percentageValueFiltered : 0}
                                    isPercentage
                                />
                            )}
                        </div>
                    </>
                )}
                {recalculateAllocationBagTotal ? (
                    <div className="item item--loading">
                        <span className="headerTitle">{formatMessage("Recalculating").toUpperCase()}</span>
                    </div>
                ) : null}
                <div className="item">
                    <span className="headerTitle">{formatMessage("Total").toUpperCase()}</span>
                </div>
                <div className="item">
                    <CartValue
                        dataTest={DATATEST.ASSORTMENTS.FOOTER.TOTAL_PRODUCTS}
                        label={formatMessage("Products")}
                        value={totalProducts}
                    />
                </div>
                <div className="item">
                    <CartValue
                        dataTest={DATATEST.ASSORTMENTS.FOOTER.TOTAL_ROWS}
                        label={formatMessage("Rows")}
                        value={totalItemsCount}
                    />
                </div>
                {totals.canShowTotals && (
                    <div className="item">
                        <CartValue
                            dataTest={DATATEST.ASSORTMENTS.FOOTER.TOTAL_UNITS}
                            label={formatMessage("Units")}
                            value={totals.quantity}
                        />
                    </div>
                )}
                {totals.canShowTotals && (
                    <div className="item">
                        <CartValue
                            dataTest={DATATEST.ASSORTMENTS.FOOTER.TOTAL_COST}
                            label={formatMessage("Cost")}
                            value={totals.wholesale}
                            isCurrency={true}
                            currency={currency}
                        />{" "}
                    </div>
                )}
                {totals.canShowTotals && (
                    <div className="item">
                        <CartValue
                            dataTest={DATATEST.ASSORTMENTS.FOOTER.TOTAL_RETAIL}
                            label={formatMessage("Retail")}
                            value={totals.retail}
                            isCurrency={true}
                            currency={currency}
                        />
                    </div>
                )}
                {totals.canShowTotals && (
                    <div className="item">
                        <CartValue
                            dataTest={DATATEST.ASSORTMENTS.FOOTER.TOTAL_MARKUP}
                            label={formatMessage("Markup")}
                            value={totals.retail > 0 ? percentageValue : 0}
                            isPercentage
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
